import { useState, useCallback } from 'react'
import { useRecoilState } from 'recoil'

import DeskService from '../services/DeskService'
import { sessionState } from '../state'
import Database from '../types/Database'
import {
  DeskFormData,
  UseDeskFormReturn,
  UseDeskFormOptions
} from '../types/ServiceTypes'

/**
 * Custom hook for managing desk form state and operations
 * Handles form validation, submission, and integration with DeskService
 */
export function useDeskForm({
  user,
  desk,
  onCreate,
  onUpdate,
  preSelectedOffice
}: UseDeskFormOptions): UseDeskFormReturn {
  const [session, setSession] = useRecoilState(sessionState)

  // Initialize form data based on whether we're editing or creating
  const getInitialFormData = useCallback(
    (): DeskFormData => ({
      name: desk?.name || '',
      officeName: desk?.officeName || preSelectedOffice?.name || '',
      labels: desk?.labels || '',
      availableTo: desk?.availableTo || ''
    }),
    [desk, preSelectedOffice]
  )

  const [formData, setFormData] = useState<DeskFormData>(() =>
    getInitialFormData()
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [successMessage, setSuccessMessage] = useState('')

  /**
   * Update a specific form field
   */
  const updateField = useCallback(
    (field: keyof DeskFormData, value: string) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
      // Clear validation errors when user starts typing
      if (validationErrors.length > 0) {
        setValidationErrors([])
      }
    },
    [validationErrors.length]
  )

  /**
   * Clear success message
   */
  const clearMessage = useCallback(() => {
    setSuccessMessage('')
  }, [])

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData(getInitialFormData())
    setValidationErrors([])
    setSuccessMessage('')
    setIsSubmitting(false)
  }, [getInitialFormData])

  /**
   * Submit the form
   */
  const submitForm = useCallback(async () => {
    if (isSubmitting) return

    // Validate form data
    const errors = DeskService.validateDeskData(formData)
    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    // Check user authentication
    if (!user?.uid || user.uid === '') {
      setValidationErrors(['User authentication required'])
      return
    }

    setIsSubmitting(true)
    setValidationErrors([])

    try {
      if (!desk) {
        // Create new desk
        const result = await DeskService.createDesk(
          formData,
          user,
          session.offices || []
        )

        // Update session state if new office was created
        if (result.isNewOffice && result.office) {
          const offices: Database.Office[] = session.offices || []
          const newOfficeList = [...offices, result.office]
          setSession(old => ({
            ...old,
            offices: newOfficeList
          }))
        }

        setSuccessMessage(
          result.isNewOffice
            ? 'Created new office and desk'
            : 'Created new desk'
        )

        // Reset form after successful creation
        setFormData({
          name: '',
          officeName: formData.officeName, // Keep office name for convenience
          labels: '',
          availableTo: ''
        })

        onCreate?.()
      } else {
        // Update existing desk
        if (!desk.id) {
          throw new Error('Desk ID is required for update')
        }

        await DeskService.updateDesk(desk.id, {
          name: formData.name,
          labels: formData.labels,
          availableTo: formData.availableTo
        })

        setSuccessMessage('Desk changes saved')
        onUpdate?.(desk.id)
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      setValidationErrors([
        error instanceof Error ? error.message : 'An unexpected error occurred'
      ])
    } finally {
      setIsSubmitting(false)
    }
  }, [
    formData,
    isSubmitting,
    user,
    desk,
    session.offices,
    setSession,
    onCreate,
    onUpdate
  ])

  return {
    formData,
    isSubmitting,
    validationErrors,
    successMessage,
    updateField,
    submitForm,
    clearMessage,
    resetForm
  }
}

export default useDeskForm
